﻿// Dashboard officiating section for UMPIRE-role users.
//
// Replaced the old summary-tile-plus-modal pattern with an inline
// section that shows the upcoming weekend games directly on the
// dashboard (per product ask: "on their dashboard, show all upcoming
// weekend games in a designer card layout"). Reuses the same
// UmpireGameCard component the full umpire workspace uses, so the two
// surfaces always look identical.
//
// The section shows at most FIRST_N cards inline; anything beyond that
// gets a "See all N games" link that jumps to the full umpire-polls
// workspace where the tabbed layout lives.

import React, { useCallback, useEffect, useState } from "react";
import {
  Gavel, ChevronRight, Loader2, Sparkles, CalendarDays
} from "lucide-react";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";
import UmpireGameCard from "./UmpireGameCard";
import { api } from "../api";

const FIRST_N = 3;   // how many games to show inline on the dashboard

export default function OfficiatingPollsCard({ navigate }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");
  const [busyId,  setBusyId]  = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const d = await api.listMyPolls(true /* weekendOnly */);
      setData(d);
    } catch (e) {
      setErr(e?.message || "Couldn't load games.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function nominate(matchId) {
    setBusyId(matchId);
    try {
      await api.selfNominate(matchId);
      toast.success("You're volunteered — head has been notified.");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't submit.");
    } finally { setBusyId(null); }
  }
  async function withdraw(matchId) {
    if (!(await confirm("Withdraw your volunteer for this match?"))) return;
    setBusyId(matchId);
    try {
      await api.withdrawNomination(matchId);
      toast.success("Withdrawn — head has been notified.");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't withdraw.");
    } finally { setBusyId(null); }
  }

  const polls    = data?.polls || [];
  const assigned = polls.filter(p => p.myStatus === "Assigned");
  const rest     = polls.filter(p => p.myStatus !== "Assigned");
  const inlineAssigned = assigned.slice(0, FIRST_N);
  const inlineRest     = rest.slice(0, FIRST_N);
  const totalHidden = Math.max(0,
    (assigned.length - inlineAssigned.length) +
    (rest.length     - inlineRest.length)
  );

  return (
    <section className="dashOfficiating">
      <header className="dashOfficiating-head">
        <div>
          <span className="dashOfficiating-eyebrow">
            <Gavel size={12}/> OFFICIATING · WEEKEND
          </span>
          <h2>Upcoming games</h2>
        </div>
        <button
          type="button"
          className="dashOfficiating-more"
          onClick={() => navigate?.("umpire-polls")}
        >
          Full workspace <ChevronRight size={13}/>
        </button>
      </header>

      {loading && (
        <p className="umpWorkspace-hint">
          <Loader2 size={14} className="umpCard-spin"/> Loading games…
        </p>
      )}
      {err && <div className="dashOfficiating-err">{err}</div>}

      {!loading && !err && polls.length === 0 && (
        <div className="umpWorkspace-empty">
          <CalendarDays size={24}/>
          <h4>No weekend games in the pipeline</h4>
          <p>Once the committee schedules matches for the next Saturdays or Sundays, they'll show up here.</p>
        </div>
      )}

      {!loading && !err && inlineAssigned.length > 0 && (
        <>
          <h3 className="umpWorkspace-sectionHead">
            <Sparkles size={14}/> Assigned to you
          </h3>
          <div className="umpCard-grid">
            {inlineAssigned.map(p => (
              <UmpireGameCard
                key={p.id}
                poll={p}
                busy={busyId === p.id}
                onNominate={() => nominate(p.id)}
                onWithdraw={() => withdraw(p.id)}
              />
            ))}
          </div>
        </>
      )}

      {!loading && !err && inlineRest.length > 0 && (
        <>
          <h3 className="umpWorkspace-sectionHead umpWorkspace-sectionHead--spaced">
            <CalendarDays size={14}/> Volunteer this weekend
          </h3>
          <div className="umpCard-grid">
            {inlineRest.map(p => (
              <UmpireGameCard
                key={p.id}
                poll={p}
                busy={busyId === p.id}
                onNominate={() => nominate(p.id)}
                onWithdraw={() => withdraw(p.id)}
              />
            ))}
          </div>
        </>
      )}

      {totalHidden > 0 && (
        <button
          type="button"
          className="dashOfficiating-seeAll"
          onClick={() => navigate?.("umpire-polls")}
        >
          See all {polls.length} game{polls.length === 1 ? "" : "s"} <ChevronRight size={13}/>
        </button>
      )}
    </section>
  );
}
